import { useMemo } from 'react'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { getPendingConnectedAppAuthorizePath } from './pending-connected-app.storage'
import { resolveRequirements } from './resolve-requirements'
import type { RequirementsAction } from './types'

type UseRequirementsGateProps = {
  path: string
  restricted: boolean
  isAuthenticated: boolean
}

export function useRequirementsGate({ path, restricted, isAuthenticated }: UseRequirementsGateProps): {
  action: RequirementsAction
  isLoading: boolean
} {
  const profile = useProfile(
    {},
    {
      enabled: isAuthenticated && (restricted || path === routeNames.connectedAppAuthorize.path),
    },
  )

  const bookingOnboarding = useBookingOnboarding({
    enabled: Boolean(isAuthenticated && profile.data?.is_booking_user),
  })

  const bookingOnboardingLoaded = !profile.data?.is_booking_user || !bookingOnboarding.isLoading
  const pendingConnectedAppPath = getPendingConnectedAppAuthorizePath()

  const action = useMemo(
    () =>
      resolveRequirements({
        path,
        restricted,
        isAuthenticated,
        profile: profile.data,
        bookingOnboarding: bookingOnboarding.data,
        bookingOnboardingLoaded,
        pendingConnectedAppPath,
      }),
    [
      bookingOnboarding.data,
      bookingOnboardingLoaded,
      isAuthenticated,
      path,
      pendingConnectedAppPath,
      profile.data,
      restricted,
    ],
  )

  const gateNeedsProfile = restricted || path === routeNames.connectedAppAuthorize.path

  return {
    action,
    isLoading: Boolean(
      isAuthenticated &&
      gateNeedsProfile &&
      (profile.isLoading || (Boolean(profile.data?.is_booking_user) && bookingOnboarding.isLoading)),
    ),
  }
}
